import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";

const ViewTier = () => {
    const navigate = useNavigate();
    const { tierName } = useParams<{ tierName: string }>();

    const [searchTerm, setSearchTerm] = useState("");
    const profitabilityData = {
        tierCostPerCustomer: "AED 180",
        totalTierCost: "AED 324,000",
        platformContribution: "AED 210,000",
        vendorContribution: "AED 114,000",

        roi: "18.4%",
        breakEvenSpend: "AED 3,200",

        vendors: {
            optedIn: 42,
            declined: 11,
        },

        tierRevenue: "AED 1.76M",
        netProfit: "AED 326,000",
    };

    // Tier data (same as in manage-customers/index.tsx)
    const loyaltyData = [
        {
            name: "Bronze",
            level: "1",
            icon: "🥉",
            description:
                "Welcome to our loyalty program! Start earning points with every purchase.",
            pointsRange: "0 – 499",
            minSpend: "AED 0.00",
            discount: "5%",
            multiplier: "1x",
            benefits: [
                "5% off all purchases",
                "Earn 1 point per $1 spent",
                "100 bonus points on birthday",
            ],
        },
        {
            name: "Silver",
            level: "2",
            icon: "🥈",
            description:
                "Enjoy enhanced rewards and exclusive member-only offers.",
            pointsRange: "500 – 1,999",
            minSpend: "AED 250.00",
            discount: "10%",
            multiplier: "1.25x",
            benefits: [
                "10% off all purchases",
                "Earn 1.25 points per $1 spent",
                "Free shipping over $50",
                "Early access to sales",
            ],
        },
        {
            name: "Gold",
            level: "3",
            icon: "🥇",
            description:
                "Premium status with priority service and exclusive benefits.",
            pointsRange: "2,000 – 4,999",
            minSpend: "AED 1,000.00",
            discount: "15%",
            multiplier: "1.5x",
            benefits: [
                "15% off all purchases",
                "Free express shipping",
                "Priority support",
                "Invitation to exclusive events",
            ],
        },
        {
            name: "Platinum",
            level: "4",
            icon: "💎",
            description:
                "Elite tier with maximum rewards and personalized experiences.",
            pointsRange: "5,000 – 9,999",
            minSpend: "AED 2,500.00",
            discount: "20%",
            multiplier: "2x",
            benefits: [
                "20% off all purchases",
                "Dedicated account manager",
                "Extended returns",
                "VIP early launches",
            ],
        },
    ];

    // Find the current tier
    const currentTier = loyaltyData.find(tier => tier.name.toLowerCase() === tierName?.toLowerCase());

    // Sample customer data (same as in manage-customers/index.tsx)
    const customerData = [
        {
            id: "CUST-00025",
            name: "Maha Sultan",
            email: "maha.sultan@gmail.com",
            phone: "+971507677889",
            status: "Active",
            tier: "Gold",
            health: 92.5,
            orders: 11,
            totalSpent: "AED 7,800.00",
            ltv: "AED 8,600.00",
            lastOrder: "11/25/2024",
        },
        {
            id: "CUST-00024",
            name: "Kareem Bashar",
            email: "kareem.bashar@hotmail.com",
            phone: "+971506566778",
            status: "At-risk",
            tier: "Silver",
            orders: 5,
            health: 70.5,
            totalSpent: "AED 2,400.00",
            ltv: "AED 2,650.00",
            lastOrder: "11/05/2024",
        },
        {
            id: "CUST-00023",
            name: "Samira Adnan",
            email: "samira.adnan@yahoo.com",
            phone: "+971505455667",
            status: "Dormant",
            tier: "Gold",
            orders: 9,
            health: 64.5,
            totalSpent: "AED 6,100.00",
            ltv: "AED 6,750.00",
            lastOrder: "10/30/2024",
        },
        {
            id: "CUST-00022",
            name: "Nader Qasim",
            email: "nader.qasim@gmail.com",
            phone: "+971504344556",
            status: "VIP",
            tier: "Gold",
            orders: 13,
            health: 92.5,
            totalSpent: "AED 9,800.00",
            ltv: "AED 10,800.00",
            lastOrder: "11/28/2024",
        },
        {
            id: "CUST-00021",
            name: "Lina Zayed",
            email: "lina.zayed@outlook.com",
            phone: "+971503233445",
            status: "Flagged",
            tier: "Silver",
            health: 100,
            orders: 7,
            totalSpent: "AED 4,200.00",
            ltv: "AED 4,650.00",
            lastOrder: "11/15/2024",
        },
        {
            id: "CUST-00020",
            name: "Walid Majid",
            email: "walid.majid@gmail.com",
            phone: "+971502122334",
            status: "Inactive",
            tier: "Bronze",
            health: 40.5,
            orders: 1,
            totalSpent: "AED 380.00",
            ltv: "AED 420.00",
            lastOrder: "05/12/2024",
        },
    ];

    // Filter customers by tier
    const tierCustomers = customerData.filter(customer => customer.tier === currentTier?.name);

    const customerColumns = [
        "CUSTOMER",
        "CONTACT",
        "STATUS",
        "ORDERS",
        "TOTAL SPENT",
        "LAST ORDER",
    ];

    // Filter customers based on search term
    const filteredCustomers = tierCustomers.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <div className="main_title">
                <h1>View Tier: {currentTier?.name}</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
                    <button onClick={() => navigate("/manage-customers")}>
                        Manage Customers
                    </button>{" "}
                    - View Tier
                </p>
            </div>

            {/* Tier Details */}
            <div className="cards">
                <div className="gap_p view_page">
                    <div className="w_33">
                        <h6>Tier Name</h6>
                        <p>{currentTier?.icon} {currentTier?.name}</p>
                    </div>
                    <div className="w_33">
                        <h6>Level</h6>
                        <p>{currentTier?.level}</p>
                    </div>
                    <div className="w_33">
                        <h6>Points Range</h6>
                        <p>{currentTier?.pointsRange}</p>
                    </div>
                    <div className="w_33">
                        <h6>Minimum Spend</h6>
                        <p>{currentTier?.minSpend}</p>
                    </div>
                    <div className="w_33">
                        <h6>Discount</h6>
                        <p>{currentTier?.discount}</p>
                    </div>
                    <div className="w_33">
                        <h6>Multiplier</h6>
                        <p>{currentTier?.multiplier}</p>
                    </div>
                    <div className="w_100">
                        <h6>Description</h6>
                        <p>{currentTier?.description}</p>
                    </div>
                    <div className="w_100">
                        <h6>Loyalty Abuse Risk</h6>
                        <div className="loyalty_abuse_risk">
                            <div className="risk_metrics">
                                <h5>Current Metrics</h5>
                                <div className="metrics_grid">
                                    <div className="metric">
                                        <span className="label">Suspicious Activities</span>
                                        <p className="value">2</p>
                                        <span className="change positive">↓ 50% from last month</span>
                                    </div>
                                    <div className="metric">
                                        <span className="label">Flagged Accounts</span>
                                        <p className="value">5</p>
                                        <span className="change neutral">No change</span>
                                    </div>
                                    <div className="metric">
                                        <span className="label">Blocked Transactions</span>
                                        <p className="value">1</p>
                                        <span className="change positive">↓ 75% from last month</span>
                                    </div>
                                    <div className="metric">
                                        <span className="label">Average Risk Score</span>
                                        <p className="value">2.3/10</p>
                                        <span className="change positive">↓ 0.2 from last month</span>
                                    </div>
                                </div>
                            </div>

                            <div className="risk_actions">
                                <h5>Recommended Actions</h5>
                                <ul>
                                    <li>Continue monitoring high-value transactions within this tier</li>
                                    <li>Review flagged accounts quarterly for manual verification</li>
                                    <li>Implement additional verification for point redemptions over AED 1,000</li>
                                    <li>Educate customers about program terms to prevent unintentional abuse</li>
                                </ul>
                            </div>    
                        </div>
                    </div>
                    {/* Profitability & ROI */}
                    <div className="w_100 tier_profitability">
                        <h6>Tier Profitability & ROI</h6>

                        <div className="metrics_grid">
                            <div className="metric">
                                <span className="label">Cost per Tier Customer</span>
                                <p className="value">{profitabilityData.tierCostPerCustomer}</p>
                            </div>

                            <div className="metric">
                                <span className="label">Total Tier Cost</span>
                                <p className="value">{profitabilityData.totalTierCost}</p>
                            </div>

                            <div className="metric">
                                <span className="label">Tier Revenue</span>
                                <p className="value">{profitabilityData.tierRevenue}</p>
                            </div>

                            <div className="metric">
                                <span className="label">Net Profit</span>
                                <p className="value positive">{profitabilityData.netProfit}</p>
                            </div>

                            <div className="metric">
                                <span className="label">ROI</span>
                                <p className="value positive">{profitabilityData.roi}</p>
                            </div>

                            <div className="metric">
                                <span className="label">Break-even Spend</span>
                                <p className="value">{profitabilityData.breakEvenSpend}</p>
                            </div>
                        </div>
                    </div>
                    {/* Contribution Split */}
                    <div className="w_100 tier_profitability">
                        <h6>Contribution Split</h6>

                        <div className="metrics_grid">
                            <div className="metric">
                                <span className="label">Platform Contribution</span>
                                <p className="value">{profitabilityData.platformContribution}</p>
                            </div>

                            <div className="metric">
                                <span className="label">Vendor Contribution</span>
                                <p className="value">{profitabilityData.vendorContribution}</p>
                            </div>
                        </div>
                    </div>
                    {/* AI Tier Optimization */}
                    <div className="w_100 ai_optimization ">
                        <h6>AI Tier Optimization Insights</h6>

                        <ul className="ai_list">
                            <li>📈 <b>Threshold Adjustment:</b> Increase entry spend to <b>AED 1,200</b> to improve ROI by ~6%</li>
                            <li>🎯 <b>Reward Tuning:</b> Reduce flat discount from <b>15% → 12%</b> while increasing experiential rewards</li>
                            <li>⚠ <b>Underperforming Perk:</b> “Free Express Shipping” used by only 18% of customers</li>
                            <li>💰 <b>Vendor ROI Prediction:</b> Expected vendor ROI: <b>22%</b> before opt-in</li>
                            <li>⚖ <b>Optimal Cost Split:</b> Platform 3% / Vendor 7%</li>
                            <li>🚨 <b>Risk Flag:</b> Tier shows early signs of reward over-utilization</li>
                        </ul>
                    </div>

                </div>
            </div>

            {/* Customers Table */}
            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <Box className="search_box">
                            <SearchBar
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setSearchTerm}
                                placeholder="Search customers..."
                            />
                        </Box>
                    </div>
                    <div className="right_s">
                        <p>Total Customers: {filteredCustomers.length}</p>
                    </div>
                </div>

                <TableContainer className="table_container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                {customerColumns.map((column, index) => (
                                    <TableCell key={index}>{column}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCustomers.map((customer, index) => (
                                <TableRow key={customer.id} hover>
                                    <TableCell>
                                        <div className="user_block">
                                            <p>
                                                <b>{customer.name}</b>
                                                <span>{customer.id}</span>
                                            </p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <p>{customer.email}</p>
                                        <span className="sub_text">{customer.phone}</span>
                                    </TableCell>
                                    <TableCell>
                                        <span className={`badge1 ${customer.status.toLowerCase()}`}>
                                            {customer.status}
                                        </span>
                                    </TableCell>
                                    <TableCell>{customer.orders}</TableCell>
                                    <TableCell>{customer.totalSpent}</TableCell>
                                    <TableCell>{customer.lastOrder}</TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </>
    );
};

export default ViewTier;
